import React from 'react';
import { useTranslation } from 'react-i18next';
import { X, TrendingUp, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { useNavigate } from 'react-router-dom';

interface UpgradeLimitModalProps {
  isOpen: boolean;
  onClose: () => void;
  limitType: 'companies' | 'collaborators' | 'services' | 'promoCodes';
  currentCount: number;
  limit: number;
}

const UpgradeLimitModal: React.FC<UpgradeLimitModalProps> = ({
  isOpen,
  onClose,
  limitType,
  currentCount,
  limit
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!isOpen) return null;

  const getLimitTitle = () => {
    switch (limitType) {
      case 'companies':
        return t('subscription.limits.companies.title');
      case 'collaborators':
        return t('subscription.limits.collaborators.title');
      case 'services':
        return t('subscription.limits.services.title');
      case 'promoCodes':
        return t('subscription.limits.promoCodes.title');
      default:
        return t('subscription.limits.generic.title');
    }
  };

  const getLimitDescription = () => {
    switch (limitType) {
      case 'companies':
        return t('subscription.limits.companies.description', { current: currentCount, limit });
      case 'collaborators':
        return t('subscription.limits.collaborators.description', { current: currentCount, limit });
      case 'services':
        return t('subscription.limits.services.description', { current: currentCount, limit });
      case 'promoCodes':
        return t('subscription.limits.promoCodes.description', { current: currentCount, limit });
      default:
        return t('subscription.limits.generic.description', { current: currentCount, limit });
    }
  };

  const handleUpgrade = () => {
    navigate('/subscription');
    onClose();
  };

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-6 relative">
          {/* Bouton fermer */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Icône */}
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-orange-100 to-red-100 dark:from-orange-900/30 dark:to-red-900/30 rounded-full flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-orange-600 dark:text-orange-400" />
            </div>
          </div>

          {/* Titre */}
          <h2 className="text-2xl font-bold text-center text-gray-900 dark:text-white mb-3">
            {getLimitTitle()}
          </h2>

          {/* Description */}
          <p className="text-gray-600 dark:text-gray-400 text-center mb-6">
            {getLimitDescription()}
          </p>

          {/* Barre de progression */}
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
              <span>{t('subscription.limits.usage')}</span>
              <span className="font-semibold">
                {currentCount} / {limit}
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 overflow-hidden">
              <div
                className="bg-gradient-to-r from-orange-500 to-red-500 h-2.5 rounded-full transition-all duration-500"
                style={{ width: `${Math.min((currentCount / limit) * 100, 100)}%` }}
              />
            </div>
          </div>

          {/* Avantages de l'upgrade */}
          <div className="bg-gradient-to-br from-primary/5 to-purple-500/5 dark:from-primary-dark/10 dark:to-purple-900/10 rounded-xl p-4 mb-6">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary dark:text-primary-light" />
              {t('subscription.limits.upgradeTitle')}
            </h3>
            <ul className="space-y-1.5 text-sm text-gray-700 dark:text-gray-300">
              <li className="flex items-start gap-2">
                <span className="text-primary dark:text-primary-light mt-0.5">•</span>
                <span>{t('subscription.limits.benefit1')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary dark:text-primary-light mt-0.5">•</span>
                <span>{t('subscription.limits.benefit2')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary dark:text-primary-light mt-0.5">•</span>
                <span>{t('subscription.limits.benefit3')}</span>
              </li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={onClose}
              variant="outline"
              className="flex-1"
            >
              {t('subscription.limits.cancel')}
            </Button>
            <Button
              onClick={handleUpgrade}
              className="flex-1 bg-gradient-to-r from-primary to-purple-600 hover:from-primary-dark hover:to-purple-700"
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              {t('subscription.limits.upgrade')}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default UpgradeLimitModal;
